import AddIcon from '@mui/icons-material/Add';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import Button from '@mui/material/Button';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { MouseEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import DashboardContentPageLayout from 'src/common/components/dashboard/DashboardContentPageLayout';
import { dashboardPrimaryActionButtonProps } from 'src/common/components/DashboardPageToolbar';
import Empty from 'src/common/components/Empty';
import usePublicPagesStore from 'src/stores/publicPagesStore';
import PagesMenuManager from './components/PagesMenuManager';

export default function Pages() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();

  const { publicPages, isLoading, deletePublicPage, fetchPublicPages } = usePublicPagesStore(s => s);

  useEffect(() => {
    if (publicPages.length > 0 || isLoading) return;
    fetchPublicPages().then(() => {
      const count = usePublicPagesStore.getState().publicPages.length;
      if (count === 0) {
        enqueueSnackbar(t('system.pages.loadError'), { variant: 'warning' });
      }
    });
  }, [publicPages.length, isLoading, fetchPublicPages, enqueueSnackbar, t]);

  const handleDelete = async (id: string, e?: MouseEvent<unknown>) => {
    e?.stopPropagation();

    const { confirmed } = await confirm({
      title: t('system.pages.deleteConfirm'),
      description: t('system.pages.deleteDescription'),
      confirmationText: t('confirmProvider.confirmationText'),
      cancellationText: t('confirmProvider.cancellationText'),
    });
    if (!confirmed) return;

    try {
      await deletePublicPage(id);
      enqueueSnackbar(t('system.pages.deleteSuccessfulMessage'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('system.pages.deleteErrorMessage'), { variant: 'error' });
    }
  };

  return (
    <DashboardContentPageLayout
      title={t('system.pages.pageTitle')}
      isLoading={isLoading && publicPages.length === 0}
      headerActions={
        <Button
          {...dashboardPrimaryActionButtonProps()}
          startIcon={<AddIcon />}
          onClick={() => navigate('/dashboard/system/pages/new')}
        >
          {t('system.pages.addPage')}
        </Button>
      }
    >
      {!isLoading && publicPages.length === 0 && (
        <Empty
          icon={<MenuBookOutlinedIcon />}
          title={t('system.pages.pageTitle')}
          description={t('system.pages.emptyNoPages')}
          primaryAction={{
            label: t('system.pages.addPage'),
            onClick: () => navigate('/dashboard/system/pages/new'),
          }}
        />
      )}
      {publicPages.length > 0 && (
        <PagesMenuManager pages={publicPages} onDelete={handleDelete} />
      )}
    </DashboardContentPageLayout>
  );
}
